import {
  Avatar,
  Card,
  CardContent,
  CardHeader,
  Grid,
  Typography,
  Divider,
  Chip,
  Box,
  Alert,
} from '@mui/material';
import OfflinePaymentCardInformation from '../new-member-applications/IndexFile/paymentInformation/offline/OfflinePaymentCardInformation';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useSingleOwnershipChangeQuery } from '../../rest/useChangeOwnership ';
import PageLoader from '../../Components/Spinner/PageLoader';
import { getStatusLabel } from '../../Utils/Label/status-label';
import { imgUrl } from '../../Helpers/utils/Constant';
import ModalsWrapper from '../../Components/Modals/ModalsWrapper';
import PdfAndImageView from '../../Components/pdfAndImageView/PdfAndImageView';
import { CommonTypoGraphy } from '../../Components/pdfAndImageView/CommonTypoGraphy';
import CommonDetailItem, {
  formatDate,
  formatValue,
} from '../../Components/CommonDetailItem/CommonDetailItem';

const SingleOwnershipChangeApplication = () => {
  const { id } = useParams();
  const {
    data: application,
    fetchData,
    loading,
  } = useSingleOwnershipChangeQuery(id !== undefined ? id : '');
  const [open, setOpen] = useState<boolean>(false);
  const [reload, setReload] = useState<boolean>(false);
  const [imageFile, setImageFile] = useState<string | null>('');

  useEffect(() => {
    fetchData();
  }, [fetchData, reload]);

  return (
    <>
      {!loading ? (
        <Box sx={{ width: '100%', mt: { xs: 4, sm: 5 }, mb: 3 }}>
          <Card
            sx={{
              width: '100%',
              borderRadius: 2,
              boxShadow: '0 1px 4px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)',
              border: '1px solid #e2e8f0',
              overflow: 'hidden',
              bgcolor: '#ffffff',
            }}
          >
            <CardHeader
              title={
                <Typography
                  variant='h6'
                  sx={{
                    fontSize: { xs: '1rem', sm: '1.125rem' },
                    fontWeight: 700,
                    color: '#1e293b',
                  }}
                >
                  Ownership Change Application Details
                </Typography>
              }

              sx={{
                borderBottom: '1px solid #edf2f7',
                py: 1.25,
                px: { xs: 2, sm: 2.5 },
                backgroundColor: '#fafbfc',
              }}
            />
            <CardContent
              sx={{
                p: { xs: 2, sm: 2.5 },
                '&:last-child': { pb: { xs: 2, sm: 2.5 } },
              }}
            >
              {application ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Rejection / Note banner if any */}
                  {application?.ownership_change_application_rejected_reason && (
                    <Alert
                      severity='error'
                      sx={{
                        borderRadius: 1.5,
                        py: 0.5,
                        px: 1.5,
                        fontSize: '0.875rem',
                        '& .MuiAlert-message': { py: 0.25 },
                      }}
                    >
                      <Typography
                        variant='subtitle2'
                        fontWeight='bold'
                        component='span'
                        sx={{ fontSize: '0.875rem' }}
                      >
                        Note / Rejection Reason:
                      </Typography>{' '}
                      {application.ownership_change_application_rejected_reason}
                    </Alert>
                  )}

                  {/* Section 1: General Information */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Box
                        sx={{
                          width: 3.5,
                          height: 15,
                          bgcolor: 'primary.main',
                          borderRadius: 1,
                        }}
                      />
                      <Typography
                        variant='subtitle2'
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.925rem',
                          color: '#0f172a',
                          letterSpacing: 0.1,
                        }}
                      >
                        General Information
                      </Typography>
                    </Box>
                    <Grid container rowSpacing={0.75} columnSpacing={2}>
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Application ID'
                        value={formatValue(
                          application.ownership_change_application_id,
                        )}
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Application Date'
                        value={formatDate(
                          application.ownership_change_application_created_at,
                        )}
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Company Name'
                        value={formatValue(application.user_member_company_name)}
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Status'
                        value={
                          application.ownership_change_application_status
                            ? getStatusLabel(
                              application.ownership_change_application_status,
                            )
                            : 'N/A'
                        }
                      />
                    </Grid>
                  </Box>

                  <Divider sx={{ borderColor: '#f1f5f9' }} />

                  {/* Section 2: Uploaded Documents */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Box
                        sx={{
                          width: 3.5,
                          height: 15,
                          bgcolor: 'primary.main',
                          borderRadius: 1,
                        }}
                      />
                      <Typography
                        variant='subtitle2'
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.925rem',
                          color: '#0f172a',
                          letterSpacing: 0.1,
                        }}
                      >
                        Uploaded Documents
                      </Typography>
                    </Box>
                    <Grid container rowSpacing={0.75} columnSpacing={2}>
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='MOCAT Approval'
                        value={
                          application?.ownership_change_application_mocat_approval_copy_file ? (
                            <CommonTypoGraphy
                              name='View MOCAT Approval'
                              onClick={() => {
                                setOpen(true);
                                setImageFile(
                                  application.ownership_change_application_mocat_approval_copy_file,
                                );
                              }}
                            />
                          ) : (
                            'N/A'
                          )
                        }
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='MOCAT Cert English'
                        value={
                          application?.ownership_change_application_mocat_certificate_english_copy ? (
                            <CommonTypoGraphy
                              name='View MOCAT Cert'
                              onClick={() => {
                                setOpen(true);
                                setImageFile(
                                  application.ownership_change_application_mocat_certificate_english_copy,
                                );
                              }}
                            />
                          ) : (
                            'N/A'
                          )
                        }
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Trade License'
                        value={
                          application?.ownership_change_application_trade_license ? (
                            <CommonTypoGraphy
                              name='View Trade License'
                              onClick={() => {
                                setOpen(true);
                                setImageFile(
                                  application.ownership_change_application_trade_license,
                                );
                              }}
                            />
                          ) : (
                            'N/A'
                          )
                        }
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Tax Return Proof'
                        value={
                          application?.ownership_change_application_tax_certificate ? (
                            <CommonTypoGraphy
                              name='View Tax Return Proof'
                              onClick={() => {
                                setOpen(true);
                                setImageFile(
                                  application.ownership_change_application_tax_certificate,
                                );
                              }}
                            />
                          ) : (
                            'N/A'
                          )
                        }
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Forwarding Letter'
                        value={
                          application?.ownership_change_application_forwarding_letter ? (
                            <CommonTypoGraphy
                              name='View Forwarding Letter'
                              onClick={() => {
                                setOpen(true);
                                setImageFile(
                                  application.ownership_change_application_forwarding_letter,
                                );
                              }}
                            />
                          ) : (
                            'N/A'
                          )
                        }
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Ownership Change Deed'
                        value={
                          application?.ownership_change_application_ownership_change_deed ? (
                            <CommonTypoGraphy
                              name='View Ownership Deed'
                              onClick={() => {
                                setOpen(true);
                                setImageFile(
                                  application.ownership_change_application_ownership_change_deed,
                                );
                              }}
                            />
                          ) : (
                            'N/A'
                          )
                        }
                      />
                    </Grid>
                  </Box>

                  <Divider sx={{ borderColor: '#f1f5f9' }} />

                  {/* Section 3: Previous Owner */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Box
                        sx={{
                          width: 3.5,
                          height: 15,
                          bgcolor: 'primary.main',
                          borderRadius: 1,
                        }}
                      />
                      <Typography
                        variant='subtitle2'
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.925rem',
                          color: '#0f172a',
                          letterSpacing: 0.1,
                        }}
                      >
                        Previous Owner
                      </Typography>
                    </Box>
                    <Grid container rowSpacing={0.75} columnSpacing={2}>
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Name'
                        value={formatValue(application.prev_company_owner_name)}
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Designation'
                        value={formatValue(application.prev_company_owner_designation)}
                      />
                      {application.prev_company_owner_photo && (
                        <CommonDetailItem
                          labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                          label='Photo'
                          value={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar
                                variant='rounded'
                                alt='Previous Owner Photo'
                                src={`${imgUrl}/${application.prev_company_owner_photo}`}
                                sx={{ width: 32, height: 32, borderRadius: 1 }}
                              />
                              <CommonTypoGraphy
                                name='View Photo'
                                onClick={() => {
                                  setOpen(true);
                                  setImageFile(application.prev_company_owner_photo);
                                }}
                              />
                            </Box>
                          }
                        />
                      )}
                    </Grid>
                  </Box>

                  <Divider sx={{ borderColor: '#f1f5f9' }} />

                  {/* Section 4: New Owner */}
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Box
                        sx={{
                          width: 3.5,
                          height: 15,
                          bgcolor: 'primary.main',
                          borderRadius: 1,
                        }}
                      />
                      <Typography
                        variant='subtitle2'
                        sx={{
                          fontWeight: 700,
                          fontSize: '0.925rem',
                          color: '#0f172a',
                          letterSpacing: 0.1,
                        }}
                      >
                        New Owner
                      </Typography>
                    </Box>
                    <Grid container rowSpacing={0.75} columnSpacing={2}>
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Name'
                        value={formatValue(application.company_owner_name)}
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Phone'
                        value={formatValue(application.company_owner_phone)}
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Designation'
                        value={formatValue(application.company_owner_designation)}
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Email'
                        value={formatValue(application.company_owner_email)}
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Address'
                        value={formatValue(application.company_owner_address)}
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='NID'
                        value={formatValue(application.company_owner_nid)}
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='NID File'
                        value={
                          application?.company_owner_nid_file ? (
                            <CommonTypoGraphy
                              name='View NID'
                              onClick={() => {
                                setOpen(true);
                                setImageFile(
                                  application.company_owner_nid_file,
                                );
                              }}
                            />
                          ) : (
                            'N/A'
                          )
                        }
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Photo'
                        value={
                          application?.company_owner_photo ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar
                                variant='rounded'
                                alt='New Owner Photo'
                                src={`${imgUrl}/${application.company_owner_photo}`}
                                sx={{ width: 32, height: 32, borderRadius: 1 }}
                              />
                              <CommonTypoGraphy
                                name='View Photo'
                                onClick={() => {
                                  setOpen(true);
                                  setImageFile(application.company_owner_photo);
                                }}
                              />
                            </Box>
                          ) : (
                            'N/A'
                          )
                        }
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Biodata'
                        value={
                          application?.company_owner_biodata ? (
                            <CommonTypoGraphy
                              name='View Biodata'
                              onClick={() => {
                                setOpen(true);
                                setImageFile(
                                  application.company_owner_biodata,
                                );
                              }}
                            />
                          ) : (
                            'N/A'
                          )
                        }
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Passport'
                        value={
                          application?.company_owner_passport ? (
                            <CommonTypoGraphy
                              name='View Passport'
                              onClick={() => {
                                setOpen(true);
                                setImageFile(
                                  application.company_owner_passport,
                                );
                              }}
                            />
                          ) : (
                            'N/A'
                          )
                        }
                      />
                      <CommonDetailItem
                        labelWidth={{ xs: '120px', sm: '135px', md: '145px' }}
                        label='Signature'
                        value={
                          application?.company_owner_signature ? (
                            <CommonTypoGraphy
                              name='View Signature'
                              onClick={() => {
                                setOpen(true);
                                setImageFile(
                                  application.company_owner_signature,
                                );
                              }}
                            />
                          ) : (
                            'N/A'
                          )
                        }
                      />
                    </Grid>
                  </Box>

                  {/* Section 5: Payment Info */}
                  {application?.paymentInfo && (
                    <>
                      <Divider sx={{ borderColor: '#f1f5f9' }} />
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Box
                            sx={{
                              width: 3.5,
                              height: 15,
                              bgcolor: 'primary.main',
                              borderRadius: 1,
                            }}
                          />
                          <Typography
                            variant='subtitle2'
                            sx={{
                              fontWeight: 700,
                              fontSize: '0.925rem',
                              color: '#0f172a',
                              letterSpacing: 0.1,
                            }}
                          >
                            Payment Information
                          </Typography>
                          <Chip
                            label={
                              application?.paymentInfo?.type === 'online'
                                ? 'Online Payment'
                                : 'Offline Payment'
                            }
                            size='small'
                            color={application?.paymentInfo?.type === 'online' ? 'primary' : 'default'}
                            variant='outlined'
                            sx={{ height: 22, fontSize: '0.75rem', fontWeight: 600, ml: 0.5 }}
                          />
                        </Box>
                        <OfflinePaymentCardInformation
                          data={application?.paymentInfo}
                        />
                      </Box>
                    </>
                  )}
                </Box>
              ) : (
                <Typography
                  variant='body1'
                  fontWeight='bold'
                  color='text.secondary'
                  textAlign='center'
                  py={4}
                >
                  No Data found
                </Typography>
              )}
            </CardContent>
          </Card>

          <ModalsWrapper
            modalData={{ title: 'View Document' }}
            setShowModal={setOpen}
            showModal={open}
          >
            <PdfAndImageView file={imageFile} />
          </ModalsWrapper>
        </Box>
      ) : (
        <PageLoader />
      )}
    </>
  );
};

export default SingleOwnershipChangeApplication;

